import React, { useState } from 'react';
import {
    Box,
    CircularProgress,
    TableHead,
    TableRow,
    TableCell,
    Table,
    Typography,
    TableContainer,
    TableBody,
    Button,
    IconButton,
    Stack,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
    Switch,
} from '@mui/material';
import { Add, Delete, Close } from '@mui/icons-material';
import { useEvents } from '../hooks/useEvents';
import { useProductEvents } from '../hooks/useProductEvents';
import { Paper } from '@shared/ui/common/paper';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useToast } from '@shared/hooks/useToast';

interface EventsProps {
    productId?: string;
}

export const Events: React.FC<EventsProps> = ({ productId }) => {
    const { success, error: showError } = useToast();
    const { events, isLoading: isLoadingAllEvents, createEvent, deleteEvent, isCreating, isDeleting } = useEvents();
    const { events: productEvents, isLoading: isLoadingProductEvents, linkEvent, unlinkEvent, isLinking, isUnlinking } = useProductEvents(productId);

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newEventTitle, setNewEventTitle] = useState('');
    const [newEventDates, setNewEventDates] = useState<Date[]>([]);

    const isLoading = isLoadingAllEvents || isLoadingProductEvents;

    // Get linked event IDs for quick lookup
    const linkedEventIds = new Set(productEvents?.map(e => e.id) || []);

    const handleCreateEvent = async () => {
        if (!newEventTitle.trim() || newEventDates.length === 0) {
            showError('Please provide a title and at least one date');
            return;
        }

        try {
            const dates = newEventDates.map(date => format(date, 'yyyy-MM-dd'));
            await createEvent({
                title: newEventTitle.trim(),
                dates,
            });
            success('Event created successfully');
            setCreateDialogOpen(false);
            setNewEventTitle('');
            setNewEventDates([]);
        } catch (err) {
            showError('Failed to create event');
        }
    };

    const handleDeleteEvent = async (eventId: string) => {
        if (!confirm('Are you sure you want to delete this event? This will unlink it from all products.')) {
            return;
        }

        try {
            await deleteEvent(eventId);
            success('Event deleted successfully');
        } catch (err) {
            showError('Failed to delete event');
        }
    };

    const handleToggleEventLink = async (eventId: string, checked: boolean) => {
        try {
            if (checked) {
                await linkEvent(eventId);
                success('Event linked to product');
            } else {
                await unlinkEvent(eventId);
                success('Event unlinked from product');
            }
        } catch (err) {
            showError(`Failed to ${checked ? 'link' : 'unlink'} event`);
        }
    };

    const handleAddDate = () => {
        setNewEventDates([...newEventDates, new Date()]);
    };

    const handleRemoveDate = (index: number) => {
        setNewEventDates(newEventDates.filter((_, i) => i !== index));
    };

    const handleDateChange = (index: number, date: Date | null) => {
        if (!date) return;
        const updatedDates = [...newEventDates];
        updatedDates[index] = date;
        setNewEventDates(updatedDates);
    };

    if (isLoading) {
        return (
            <Paper>
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress />
                </Box>
            </Paper>
        );
    }

    return (
        <>
            <Paper sx={{ p: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Events</Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setCreateDialogOpen(true)}
                        size="small"
                    >
                        Create Event
                    </Button>
                </Stack>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {productId && <TableCell padding="checkbox">Linked</TableCell>}
                                <TableCell>Title</TableCell>
                                <TableCell>Dates</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={productId ? 4 : 3} align="center">
                                        <Typography color="text.secondary" py={2}>
                                            No events found. Create your first event to get started.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                events?.map((event) => {
                                    const isLinked = linkedEventIds.has(event.id);
                                    const sortedDates = [...event.eventDates].sort((a, b) =>
                                        new Date(a.date).getTime() - new Date(b.date).getTime()
                                    );

                                    // Group dates by year
                                    const datesByYear = sortedDates.reduce((acc, eventDate) => {
                                        const year = format(new Date(eventDate.date), 'yyyy');
                                        if (!acc[year]) {
                                            acc[year] = [];
                                        }
                                        acc[year].push(eventDate);
                                        return acc;
                                    }, {} as Record<string, typeof sortedDates>);

                                    return (
                                        <TableRow key={event.id}>
                                            {productId && (
                                                <TableCell padding="checkbox">
                                                    <Switch
                                                        checked={isLinked}
                                                        onChange={(_, checked) => handleToggleEventLink(event.id, checked)}
                                                        disabled={isLinking || isUnlinking}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Typography variant="body2">{event.title}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                    <span key={new Date().getFullYear()}>{new Date().getFullYear()}:</span>
                                                    {datesByYear[new Date().getFullYear()].map((date, index) => (
                                                        <span key={date.id}>
                                                            {format(new Date(date.date), 'MMM dd')}
                                                            {index < datesByYear[new Date().getFullYear()].length - 1 && ', '}
                                                        </span>
                                                    ))}
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right">
                                                {event.companyId && (
                                                    <Tooltip title="Delete event">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteEvent(event.id)}
                                                            disabled={isDeleting}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper >

            {/* Create Event Dialog */}
            < Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Create New Event</Typography>
                        <IconButton onClick={() => setCreateDialogOpen(false)} size="small">
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                label="Event Title"
                                value={newEventTitle}
                                onChange={(e) => setNewEventTitle(e.target.value)}
                                placeholder="e.g., Black Friday, Christmas Sale"
                                required
                            />

                            <Box>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="subtitle2">Event Dates</Typography>
                                    <Button size="small" onClick={handleAddDate} startIcon={<Add />}>
                                        Add Date
                                    </Button>
                                </Stack>

                                <Stack spacing={2}>
                                    {newEventDates.map((date, index) => (
                                        <Stack key={index} direction="row" spacing={2} alignItems="center">
                                            <DatePicker
                                                label={`Date ${index + 1}`}
                                                value={date}
                                                onChange={(newDate) => handleDateChange(index, newDate)}
                                                sx={{ flex: 1 }}
                                            />
                                            <IconButton
                                                onClick={() => handleRemoveDate(index)}
                                                color="error"
                                                size="small"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Stack>
                                    ))}

                                    {newEventDates.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                            Click "Add Date" to add event dates
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateEvent}
                        variant="contained"
                        disabled={!newEventTitle.trim() || newEventDates.length === 0 || isCreating}
                        startIcon={isCreating ? <CircularProgress size={16} /> : undefined}
                    >
                        {isCreating ? 'Creating...' : 'Create Event'}
                    </Button>
                </DialogActions>
            </Dialog >
        </>
    );
};
